/* eslint-disable max-lines-per-function */
import { helper } from '@/standardChartManager/standardChart';
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { createDefaultWatermark } from '../../../helper/standardChart';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicRankingTwoWayHorizontalBar',
  name_en: 'Dynamic Ranking Two-way Horizontal Bar',
  // name_zh: '动态排名双向条形图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    x2: amount => amount > 1,
    y: amount => amount > 1,
    group: amount => amount > 1,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    x2: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    group: { itemType: [DEFINE.DATE], category: DEFINE.SINGLE }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    x2: string;
    y: string;
    group: string;
  };
  animationDuration: number;
  dvShowWaterMark: boolean;
}

export const dynamicRankingTwoWayHorizontalBar = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  withTimeline: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const chartViewRect = $dom.getBoundingClientRect();
    const presetOption = hook.buildStandardChartPresetOption(api);
    const {
      dataIndex = 0,
      encoding: { x: leftxAttribute, x2: rightxAttribute, group: groupBy, y },
      animationDuration: updateFrequency,
      dvShowWaterMark = false
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * ------------------------- data
    const x0 = rightxAttribute;
    const x1 = leftxAttribute;
    const formatData = [];
    const dataset = data.values.map(el => {
      return {
        name: `${el[y]}`,
        value: +el[x0],
        value2: +el[x1],
        date: Number(el[groupBy].replaceAll('-', '')),
        __formatDate: el[groupBy]
      };
    });
    const { years, originDates, names } = util.dataEncoding.encoding(dataset, {
      years: {
        field: '__formatDate',
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      },
      originDates: {
        field: 'date',
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      },
      names: { field: 'name', transforms: [{ type: 'deDuplication' }] }
    });

    // * ------------------------- chart
    years.forEach(year => {
      names.forEach(item => {
        const d = util.dataTransform.transform(dataset.slice(), [
          { type: 'find', cb: dt => dt.name === item && dt.__formatDate === year }
        ]);
        const returnData = d || {
          name: item,
          value: '',
          value2: '',
          date: Number(year.replaceAll('-', '')),
          __formatDate: year
        };
        formatData.push(returnData);
      });
    });

    // 补齐数据
    const fillDataset = [];
    originDates.forEach((date, index) => {
      names.forEach(name => {
        const dataItem = util.dataTransform.transform(dataset.slice(), [
          { type: 'find', cb: item => item.name === name && item.date === date }
        ]);

        if (!dataItem) {
          fillDataset.push({
            name,
            value: 0,
            value2: 0,
            date,
            __formatDate: years[index]
          });
        }
      });
    });
    dataset.push(...fillDataset);

    const maxDisplayNumber = 8;
    const containerWidth = $dom.clientWidth;
    const grid = [
      {
        dvBoxLayout: null,
        width: 0.4 * containerWidth,
        left: containerWidth * 0.4 + (containerWidth - 500) / 10 + 100,
        top: 20,
        bottom: 4
      },
      {
        dvBoxLayout: null,
        width: 0.4 * containerWidth,
        top: 20,
        left: (containerWidth - 500) / 10,
        bottom: 4
      }
    ];
    const yMaxNum = Math.floor((chartViewRect.height - grid[0].top - grid[0].bottom) / 15);

    // 配置项
    const option = {
      animationDuration: updateFrequency,
      animationDurationUpdate: updateFrequency,
      grid,
      dataZoom: [],
      tooltip: {
        formatter: item => {
          const value = hook.formatDataNumberValue(item?.value[0], {
            type: 'table',
            unit: dataMetaInfo[item.seriesName]?.unit
          });
          // prettier-ignore
          const itemObj = {
            type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
            color: item.color,
            name: item.seriesName,
            value: value.value + value.unit
          }
          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: item?.name,
            items: [itemObj]
          });
        }
      },
      yAxis: [
        {
          type: 'category',
          show: true,
          // name: '111',
          max: Math.min(maxDisplayNumber, yMaxNum - 1),
          splitLine: {
            show: false
          },
          axisLabel: {
            margin: 50,
            width: 100,
            align: 'center',
            formatter(value) {
              if (String(value).length > 5) {
                return `${value.slice(0, 5)}...`;
              }
              return value;
            }
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        },
        {
          type: 'category',
          show: true,
          gridIndex: 1,
          max: Math.min(maxDisplayNumber, yMaxNum - 1),
          position: 'right',
          splitLine: {
            show: false
          },
          axisLabel: {
            show: false,
            fontFamily: presetOption.textStyle.fontFamily
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      xAxis: [
        {
          type: 'value',
          show: true,
          axisLabel: {
            formatter(value) {
              const result = hook.formatDataNumberValue(value);
              if (Number(result.value >= 0)) {
                return result.value + result.unit;
              }
              return '';
            },
            dvAlignEdge: true,
            hideOverlap: true,
            showMaxLabel: true,
            showMinLabel: true,
            fontFamily: presetOption.textStyle.fontFamily
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        },
        {
          type: 'value',
          show: true,
          gridIndex: 1,
          inverse: true,
          axisLabel: {
            formatter(value) {
              const result = hook.formatDataNumberValue(value);
              if (Number(result.value >= 0)) {
                return result.value + result.unit;
              }
              return '';
            },
            dvAlignEdge: true,
            hideOverlap: true,
            showMaxLabel: true,
            showMinLabel: true,
            fontFamily: presetOption.textStyle.fontFamily
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      series: [
        {
          type: 'bar',
          name: x0,
          realtimeSort: true,
          dvValueKey: 'value',
          label: {
            show: false,
            formatter(data) {
              console.log('data: ', data);
              if (data.data.value[0] === '') {
                return '--';
              }
              const result = hook.formatDataNumberValue(data.data.value[0], {
                type: 'table',
                unit: dataMetaInfo[data.seriesName]?.unit
              });
              return result.value + result.unit;
            }
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        },
        {
          type: 'bar',
          realtimeSort: true,
          name: x1,
          dvValueKey: 'value2',
          xAxisIndex: 1,
          yAxisIndex: 1,
          label: {
            show: false,
            position: 'left',
            formatter(data) {
              if (data.data.value[0] === '') {
                return '--';
              }
              const result = hook.formatDataNumberValue(data.data.value[0], {
                type: 'table',
                unit: dataMetaInfo[data.seriesName]?.unit
              });
              return result.value + result.unit;
            }
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      graphic: createDefaultWatermark(chartViewRect)
    };

    return renderApi => {
      const renderParams = { option: renderApi.mergeChartOptionProxy(option) };

      const timelineIns = renderApi.renderTimelineProxy(years as string[]);
      renderApi.renderLegendProxy(renderParams.option?.legend?.data);
      const chartIns = renderApi.renderChartProxy(renderParams, { theme: 'pc-wencai-light' });

      const dynamicHistogramHelper = new helper.DynamicHistogramHelper(
        chartIns,
        renderParams.option,
        formatData,
        {
          watermarkFormatter(date) {
            const index = originDates.indexOf(date);
            return dvShowWaterMark ? years[index] : '';
          }
        }
      );

      function chartPlayByIndex(index, animate) {
        dynamicHistogramHelper.play(originDates[index], animate);
      }
      // ! bugs timer
      setTimeout(() => {
        chartPlayByIndex(originDates.length - 1, 'axisClick');
      }, 16);

      if (timelineIns instanceof api.ui.Dropdown) {
        timelineIns?.on?.('change', e => {
          chartPlayByIndex(e.index, e.causeBy !== 'axisClick');
        });
      } else {
        timelineIns?.on?.('change', e => {
          chartPlayByIndex(e.index, e.causeBy !== 'axisClick' && e.causeBy !== 'axisMarkerDrag');
        });
      }
    };
  }
});
